/**
 * phase-forms-workflow-follow-up (Item A) — Load Required Forms picker.
 *
 * Replaces the old "dump everything" behaviour: a checklist of the applicable
 * requirements for this submission's region/type/activity (the SAME
 * definitions the Submission Requirements tab reads), each with its
 * Required / Conditional / Optional badge. DEFAULT: Required checked,
 * Conditional/Optional unchecked. Already-loaded rows render checked + disabled
 * (the clearer idiom — they read as "present" and can't be unchecked, so Load
 * only ever creates NEW rows). Load creates only the chosen, not-present rows.
 */
import { MovableDialog } from '@/components/shared/MovableDialog';
import { useState, type ReactElement } from 'react';
import type { RequirementPickerItem } from '@/api/v2Client';

const LEVEL_STYLE: Record<RequirementPickerItem['requirement'], { label: string; color: string }> = {
  required: { label: '✅ Required', color: '#2e7d32' },
  conditional: { label: '🟡 Conditional', color: '#b45309' },
  optional: { label: '⚪ Optional', color: '#6b7280' },
};

export function LoadRequiredPicker({
  items,
  onConfirm,
  onCancel,
}: {
  items: RequirementPickerItem[];
  onConfirm: (selection: string[]) => Promise<void>;
  onCancel: () => void;
}): ReactElement {
  // Default: Required checked, everything else unchecked. Already-loaded rows
  // are shown checked but locked (they can't be re-loaded).
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const it of items) init[it.key] = it.defaultChecked;
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadable = items.filter((it) => !it.alreadyLoaded);
  const allLoadableChecked = loadable.length > 0 && loadable.every((it) => checked[it.key]);
  const chosen = loadable.filter((it) => checked[it.key]).map((it) => it.key);

  const toggle = (key: string): void => setChecked((p) => ({ ...p, [key]: !p[key] }));
  const toggleAll = (): void => {
    const next = !allLoadableChecked;
    setChecked((p) => {
      const copy = { ...p };
      for (const it of loadable) copy[it.key] = next;
      return copy;
    });
  };

  const confirm = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await onConfirm(chosen);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load forms');
      setSaving(false);
    }
  };

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="submission.loadRequired"
      title={<span id="load-required-title">Load required forms</span>}
      ariaLabelledBy="load-required-title"
      testId="load-required-picker"
      onClose={saving ? undefined : onCancel}
      escDisabled={saving}
      closeOnBackdrop={!saving}
      zIndex={1000}
    >
      <>
        <p style={{ margin: '0 0 12px 0', fontSize: 13, color: 'var(--text-muted, #6b7280)' }}>
          Choose which requirements to add to this submission. Required items are pre-selected.
        </p>

        {items.length === 0 ? (
          <div data-testid="picker-empty" style={{ padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            No requirements are defined for this submission&apos;s region and type.
          </div>
        ) : (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              <input
                type="checkbox"
                data-testid="picker-select-all"
                checked={allLoadableChecked}
                onChange={toggleAll}
                disabled={loadable.length === 0}
              />
              Select all ({loadable.length} loadable)
            </label>
            <div style={{ overflowY: 'auto', border: '1px solid var(--border, #e5e7eb)', borderRadius: 6 }}>
              {items.map((it) => {
                const level = LEVEL_STYLE[it.requirement];
                return (
                  <label
                    key={it.key}
                    data-testid={`picker-row-${it.key}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--border, #f1f5f9)',
                      opacity: it.alreadyLoaded ? 0.65 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      data-testid={`picker-check-${it.key}`}
                      checked={it.alreadyLoaded ? true : !!checked[it.key]}
                      disabled={it.alreadyLoaded}
                      onChange={() => toggle(it.key)}
                    />
                    <span style={{ flex: 1, fontSize: 13 }}>{it.displayName}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                      {it.formType}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: level.color }}>{level.label}</span>
                    {it.alreadyLoaded && (
                      <span data-testid={`picker-present-${it.key}`} style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                        present
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </>
        )}

        {error && <div style={{ color: 'var(--red, #dc2626)', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            data-testid="picker-cancel"
            onClick={onCancel}
            disabled={saving}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--border, #d1d5db)',
              borderRadius: 6,
              background: 'var(--surface, #fff)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="picker-load"
            onClick={() => void confirm()}
            disabled={saving || chosen.length === 0}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 6,
              background: 'var(--accent, #2563eb)',
              color: '#fff',
              cursor: saving || chosen.length === 0 ? 'not-allowed' : 'pointer',
              opacity: saving || chosen.length === 0 ? 0.6 : 1,
            }}
          >
            {saving ? 'Loading…' : `Load ${chosen.length} form${chosen.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </>
    </MovableDialog>
  );
}
